import { signOut } from 'aws-amplify/auth'
import { Bell, Menu } from 'lucide-react'
import { AnimatePresence, motion as m } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import logoutSvg from '@/assets/icons/logout.svg'
import bookingSvg from '@/assets/images/booking.svg'
import { useAuthContext } from '@/contexts/auth-context'
import { env } from '@/env'
import { cn } from '@/lib/utils'

import { AgencyChainMenuOption } from './agency-chain-meny-option'
import { BookingEngineMenuOption } from './booking-engine-menu-option'
import { CurrencyLangToggle } from './currency-lang-toggle'
import { ZendeskHelpButton } from './zendesk-help-button'

interface NavbarProps {
  opened: boolean
  handleToggle: () => void
  className?: string
}

export function Navbar({
  opened: propOpened,
  handleToggle: propHandleToggle,
  className,
}: NavbarProps) {
  const { t } = useTranslation('common')

  const { user } = useAuthContext()
  const [opened, setOpened] = useState(propOpened)

  // Handle screen size changes
  useEffect(() => {
    const checkScreenSize = () => {
      const isSmall = window.innerWidth < 768 // md breakpoint

      setOpened(isSmall ? false : propOpened)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [propOpened])

  // Handle toggle
  const handleToggle = () => {
    setOpened(!opened)
    propHandleToggle()
  }

  const handleLogout = () => {
    signOut()
      .then(() => {
        localStorage.clear()
        sessionStorage.clear()
        window.location.href = `${env.VITE_HUB_ROUTE}/dashboard`
      })
      .catch(error => {
        console.error('Error signing out: ', error)
      })
  }

  return (
    <m.aside
      animate={{
        width: opened ? 273 : 72,
        transition: { duration: 0.4, ease: 'easeOut' },
      }}
      className={cn(
        'bg-[#1B144B] flex flex-col gap-4 p-4 text-white sticky max-h-[calc(100vh-16px)] top-2 rounded-[10px]',
        className,
      )}
      id="new-sidebar"
    >
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggle}
            className="flex shrink-0 items-center justify-center size-10 rounded-md bg-primaryDark hover:bg-[#322c5d] backdrop-blur-md duration-200"
            id="sidebar-toggle"
          >
            <Menu className="size-6 text-white" />
          </button>
          <AnimatePresence>
            {opened && (
              <m.div
                initial={{ opacity: 0, x: -50 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.3 },
                }}
                exit={{
                  opacity: 0,
                  x: -50,
                  transition: { duration: 0.3 },
                }}
                className="flex flex-1 items-center gap-2"
              >
                <img src={bookingSvg} alt="Booking" />
                <button className="flex ml-auto items-center justify-center size-10 rounded-md bg-transparent enabled:hover:bg-white/10 duration-200">
                  <Bell className="size-5" />
                </button>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="space-y-2 overflow-auto flex-1">
        <AgencyChainMenuOption />
        <BookingEngineMenuOption opened={opened} />
      </div>

      <div className={cn('space-y-2 mt-auto')}>
        <div className="flex items-center justify-start">
          <AnimatePresence>
            {opened && (
              <m.div
                initial={{ opacity: 0, x: -50 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.3 },
                }}
                exit={{
                  opacity: 0,
                  x: -50,
                  transition: { duration: 0.3 },
                }}
                className="flex text-nowrap items-center line-clamp-1 w-full h-10 text-white px-2"
              >
                {user?.signInUserSession.idToken.payload.name as string}
              </m.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {!opened && (
              <m.div
                initial={{ opacity: 0, x: -50 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.3, delay: 0.4 },
                }}
                exit={{
                  x: -50,
                  opacity: 0,
                  transition: { duration: 0.3 },
                }}
                className="flex items-center justify-center size-10 bg-white/20 text-white rounded-md"
              >
                {
                  (
                    user?.signInUserSession.idToken.payload.given_name as string
                  )?.[0]
                }
                {
                  (
                    user?.signInUserSession.idToken.payload
                      .family_name as string
                  )?.[0]
                }
              </m.div>
            )}
          </AnimatePresence>
        </div>

        <ZendeskHelpButton isOpen={opened} />

        <m.button
          className="flex gap-2 items-center justify-start w-full h-10 text-white bg-transparent hover:bg-white/10 duration-200 border border-white/50 rounded-md"
          onClick={handleLogout}
        >
          <img className="size-4 ml-2.5 mr-1" src={logoutSvg} alt="Logout" />

          <AnimatePresence>
            {opened && (
              <m.span
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.3 } }}
                exit={{ x: -50, opacity: 0, transition: { duration: 0.3 } }}
                className="text-white text-nowrap"
              >
                {t('--actions-logout')}
              </m.span>
            )}
          </AnimatePresence>
        </m.button>
      </div>

      <CurrencyLangToggle open={opened} />

      <m.span
        initial={{ x: -50, opacity: 0 }}
        animate={{
          x: 0,
          opacity: opened ? 1 : 0,
          transition: { duration: opened ? 0.3 : 0, delay: opened ? 0.2 : 0 },
        }}
        exit={{ x: -50, opacity: 0, transition: { duration: 0.3 } }}
        className={cn('block text-xs text-nowrap text-[#7C71C3]')}
      >
        B2B.reservas © 2013 - {new Date().getFullYear()}
      </m.span>
    </m.aside>
  )
}
